<template>
  <div>
    <h2 class="page-title">创建 SN</h2>

    <div class="card-panel">
      <h3 class="section-title">单个创建</h3>
      <p class="section-desc">选择可用号段并输入 5 位 SN段2主体，系统会自动生成校验码和预览内容。</p>
      <el-form :model="singleForm" label-position="top" class="form-grid">
        <el-form-item label="选择号段">
          <el-select v-model="singleForm.segmentId" placeholder="请选择号段" style="width:100%;">
            <el-option
              v-for="segment in activeSegments"
              :key="segment.id"
              :label="`SN段1 ${segment.segmentCode} / ${segment.productName || '未设置产品名'}`"
              :value="segment.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="SN段2主体">
          <el-input v-model="singleForm.customCode" maxlength="5" placeholder="5位，大写字母和数字" />
        </el-form-item>
      </el-form>
      <el-alert
        title="系统会自动按“5位主体 + 1位校验码”生成最终 SN段2。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom:16px;"
      />
      <div class="action-row">
        <el-button type="primary" @click="handleSingle">创建 SN</el-button>
      </div>
    </div>

    <div class="card-panel" v-if="singlePreview.formattedSn">
      <h3 class="section-title">单个创建预览</h3>
      <el-descriptions :column="1" border>
        <el-descriptions-item label="SN段2主体">{{ singlePreview.coreCode }}</el-descriptions-item>
        <el-descriptions-item label="SN段2完整值">{{ singlePreview.customCode }}</el-descriptions-item>
        <el-descriptions-item label="格式化SN"><span class="code-text">{{ singlePreview.formattedSn }}</span></el-descriptions-item>
        <el-descriptions-item label="原始SN"><span class="code-text">{{ singlePreview.sn }}</span></el-descriptions-item>
        <el-descriptions-item label="二维码链接"><span class="code-text">{{ singlePreview.qrUrl }}</span></el-descriptions-item>
      </el-descriptions>
      <div class="preview-grid">
        <div class="preview-card">
          <div class="preview-card-title">二维码预览</div>
          <canvas v-show="singlePreview.formattedSn" ref="singleQrCanvas" width="220" height="220"></canvas>
        </div>
        <div class="preview-card">
          <div class="preview-card-title">条形码预览</div>
          <svg v-show="singlePreview.formattedSn" ref="singleBarcode"></svg>
        </div>
      </div>
    </div>

    <div class="card-panel">
      <h3 class="section-title">批量创建</h3>
      <p class="section-desc">按手工输入、顺序生成或随机生成方式批量创建 SN。</p>
      <el-form :model="batchForm" label-position="top" class="form-grid">
        <el-form-item label="选择号段">
          <el-select v-model="batchForm.segmentId" placeholder="请选择号段" style="width:100%;">
            <el-option
              v-for="segment in activeSegments"
              :key="segment.id"
              :label="`SN段1 ${segment.segmentCode} / ${segment.productName || '未设置产品名'}`"
              :value="segment.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="生成方式">
          <el-radio-group v-model="batchForm.mode">
            <el-radio label="manual">手工输入</el-radio>
            <el-radio label="sequence">顺序生成</el-radio>
            <el-radio label="random">随机生成</el-radio>
          </el-radio-group>
        </el-form-item>

        <template v-if="batchForm.mode === 'manual'">
          <el-form-item label="批量 SN段2主体">
            <el-input
              type="textarea"
              :rows="5"
              v-model="batchForm.manualText"
              placeholder="每行一个5位主体，例如：&#10;A1234&#10;A1235"
            />
          </el-form-item>
        </template>

        <template v-else-if="batchForm.mode === 'sequence'">
          <el-form-item label="起始值">
            <el-input v-model="batchForm.start" placeholder="例如 A0001" maxlength="5" />
          </el-form-item>
          <el-form-item label="生成数量">
            <el-input-number v-model="batchForm.count" :min="1" :max="1000" style="width:100%;" />
          </el-form-item>
          <el-form-item label="步长">
            <el-input-number v-model="batchForm.step" :min="1" :max="999999" style="width:100%;" />
          </el-form-item>
          <el-alert
            :title="sequenceSummary"
            type="info"
            :closable="false"
            show-icon
          />
        </template>

        <template v-else>
          <el-form-item label="生成数量">
            <el-input-number v-model="batchForm.count" :min="1" :max="1000" style="width:100%;" />
          </el-form-item>
          <el-form-item label="生成规则">
            <el-radio-group v-model="batchForm.randomRule">
              <el-radio label="digits">纯数字</el-radio>
              <el-radio label="alnum">数字 + 大写字母</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-alert
            :title="randomSummary"
            type="info"
            :closable="false"
            show-icon
          />
        </template>
      </el-form>
      <div class="action-row">
        <el-button type="success" @click="handleBatch">批量创建</el-button>
      </div>
    </div>

    <div class="card-panel">
      <h3 class="section-title">最近创建结果</h3>
      <div v-if="issuedRows.length" class="table-scroll">
        <el-table :data="issuedRows" width="100%">
          <el-table-column prop="formattedSn" label="格式化SN" min-width="220" />
          <el-table-column prop="customCode" label="SN段2" width="120" />
          <el-table-column prop="verifyCode" label="验证码" width="100" />
        </el-table>
      </div>
      <div v-else class="empty-panel">暂无创建结果</div>
    </div>
  </div>
</template>

<script>
import JsBarcode from 'jsbarcode'
import QRCode from 'qrcode'
import { getVendorProfile, getVendorSegments, issueBatch, issueSingle } from '../../api/vendor'

const CHECKSUM_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'

export default {
  data() {
    return {
      profile: {},
      segments: [],
      singleForm: {
        segmentId: '',
        customCode: '',
      },
      singlePreview: {
        coreCode: '',
        customCode: '',
        sn: '',
        formattedSn: '',
        qrUrl: '',
      },
      batchForm: {
        segmentId: '',
        mode: 'manual',
        manualText: '',
        start: '',
        count: 10,
        step: 1,
        randomRule: 'digits',
      },
      issuedRows: [],
    }
  },
  computed: {
    activeSegments() {
      return this.segments.filter(item => item.status === 'active')
    },
    selectedSingleSegment() {
      return this.segments.find(item => item.id === this.singleForm.segmentId)
    },
    sequenceSummary() {
      const start = (this.batchForm.start || '00000').toUpperCase()
      const count = this.batchForm.count || 0
      const step = this.batchForm.step || 1
      const stepText = step === 1 ? '步长为 1（不跳号）' : `步长为 ${step}`
      return `将从 ${start} 开始，按${stepText}生成 ${count} 个 5 位 SN主体，并自动补 1 位校验码。`
    },
    randomSummary() {
      const count = this.batchForm.count || 0
      const ruleText = this.batchForm.randomRule === 'digits' ? '纯数字' : '数字 + 大写字母'
      return `将随机生成 ${count} 个 5 位 SN主体，规则为${ruleText}，创建时自动补1位校验码。`
    },
  },
  watch: {
    'singleForm.segmentId': 'updateSinglePreview',
    'singleForm.customCode': 'updateSinglePreview',
  },
  created() {
    document.title = 'CoRecogN'
    this.loadSegments()
  },
  methods: {
    async loadSegments() {
      const { data } = await getVendorSegments()
      this.segments = data
      const firstActive = data.find(item => item.status === 'active')
      this.singleForm.segmentId = firstActive ? firstActive.id : ''
      this.batchForm.segmentId = firstActive ? firstActive.id : ''
      this.updateSinglePreview()
    },
    normalizeCoreCode(value) {
      return (value || '').trim().toUpperCase()
    },
    generateRandomCodes(count, rule) {
      const alphabet = rule === 'digits' ? '0123456789' : CHECKSUM_ALPHABET
      const items = []
      const seen = new Set()
      while (items.length < count) {
        let code = ''
        for (let index = 0; index < 5; index += 1) {
          code += alphabet[Math.floor(Math.random() * alphabet.length)]
        }
        if (seen.has(code)) {
          continue
        }
        seen.add(code)
        items.push(code)
      }
      return items
    },
    calculateChecksum(coreCode) {
      let total = 0
      coreCode.split('').forEach((char, index) => {
        total += (CHECKSUM_ALPHABET.indexOf(char) + 1) * (index + 3)
      })
      return CHECKSUM_ALPHABET[total % CHECKSUM_ALPHABET.length]
    },
    buildPreviewSn() {
      const coreCode = this.normalizeCoreCode(this.singleForm.customCode)
      if (!this.selectedSingleSegment || !/^[0-9A-Z]{5}$/.test(coreCode)) {
        return null
      }
      const now = new Date()
      const datePart = `${now.getFullYear() % 10}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
      const shortCode = (this.selectedSingleSegment.vendorShortCode || '').toUpperCase()
      if (!/^[0-9A-Z]{4}$/.test(shortCode)) {
        return null
      }
      const customCode = `${coreCode}${this.calculateChecksum(coreCode)}`
      const sn = `${datePart}${shortCode}${this.selectedSingleSegment.segmentCode}${customCode}`
      return { coreCode, customCode, sn }
    },
    formatSn(sn) {
      return sn ? `${sn.slice(0, 5)}-${sn.slice(5, 9)}-${sn.slice(9, 13)}-${sn.slice(13)}` : ''
    },
    clearPreviewCanvas() {
      if (this.$refs.singleQrCanvas) {
        const ctx = this.$refs.singleQrCanvas.getContext('2d')
        ctx.clearRect(0, 0, this.$refs.singleQrCanvas.width, this.$refs.singleQrCanvas.height)
      }
      if (this.$refs.singleBarcode) {
        this.$refs.singleBarcode.innerHTML = ''
      }
    },
    async updateSinglePreview() {
      const preview = this.buildPreviewSn()
      if (!preview) {
        this.singlePreview = { coreCode: '', customCode: '', sn: '', formattedSn: '', qrUrl: '' }
        this.$nextTick(() => this.clearPreviewCanvas())
        return
      }
      this.singlePreview = {
        ...preview,
        formattedSn: this.formatSn(preview.sn),
        qrUrl: `${window.location.origin}/#/verify?sn=${preview.sn}`,
      }
      await this.$nextTick()
      this.clearPreviewCanvas()
      await QRCode.toCanvas(this.$refs.singleQrCanvas, this.singlePreview.qrUrl, { width: 220, margin: 1 })
      JsBarcode(this.$refs.singleBarcode, preview.sn, {
        format: 'CODE128',
        displayValue: true,
        height: 70,
        margin: 10,
      })
    },
    async handleSingle() {
      try {
        const { data } = await issueSingle(this.singleForm)
        this.issuedRows = [{ formattedSn: data.formattedSn, customCode: data.customCode, verifyCode: data.verifyCode }]
        this.$message.success(data.message)
      } catch (error) {
        this.$message.error(error.response?.data?.message || '创建失败')
      }
    },
    async handleBatch() {
      try {
        const payload = {
          segmentId: this.batchForm.segmentId,
          mode: this.batchForm.mode,
        }
        if (this.batchForm.mode === 'manual') {
          payload.customCodes = this.batchForm.manualText
            .split(/\r?\n/)
            .map(item => this.normalizeCoreCode(item))
            .filter(Boolean)
        } else if (this.batchForm.mode === 'sequence') {
          payload.start = this.normalizeCoreCode(this.batchForm.start)
          payload.count = this.batchForm.count
          payload.step = this.batchForm.step
        } else {
          payload.customCodes = this.generateRandomCodes(this.batchForm.count, this.batchForm.randomRule)
        }
        const { data } = await issueBatch(payload)
        this.issuedRows = data.items
        this.$message.success(data.message)
      } catch (error) {
        this.$message.error(error.response?.data?.message || '批量创建失败')
      }
    },
  },
}
</script>
